import { createServerFn } from "@tanstack/react-start";

import { classifyAttribution } from "./attribution";

type TrackInput = {
  cid: string;
  landing_page?: string | null;
  referrer?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
};

const clean = (v: unknown, max = 500): string | null => {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t === "" ? null : t.slice(0, max);
};

/**
 * Registrerar/uppdaterar first-party attribution för aktuellt GA4 client id (cid).
 * Lagrar inga IP-adresser eller andra personuppgifter.
 */
export const trackAttribution = createServerFn({ method: "POST" })
  .inputValidator((data: TrackInput) => {
    const cid = clean(data?.cid, 100);
    if (!cid) throw new Error("missing cid");
    return {
      cid,
      landing_page: clean(data?.landing_page, 1000),
      referrer: clean(data?.referrer, 1000),
      utm_source: clean(data?.utm_source, 200),
      utm_medium: clean(data?.utm_medium, 200),
      utm_campaign: clean(data?.utm_campaign, 300),
      utm_content: clean(data?.utm_content, 300),
      utm_term: clean(data?.utm_term, 300),
      fbclid: clean(data?.fbclid, 500),
      gclid: clean(data?.gclid, 500),
    } satisfies TrackInput;
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("visitor_attribution")
      .select("id, channel")
      .eq("cid", data.cid)
      .maybeSingle();

    const now = new Date().toISOString();
    const result = classifyAttribution(data);

    // Första besöket vinner (first-touch); senare besök uppdaterar bara last_seen_at,
    // utom när det första var direct/other och vi nu har en tydligare kanal.
    if (existing) {
      const upgrade =
        (existing.channel === "direct" || existing.channel === "other") &&
        result.channel !== "direct" &&
        result.channel !== "other";
      const patch = upgrade
        ? {
            last_seen_at: now,
            channel: result.channel,
            source: result.source,
            medium: result.medium,
            campaign: result.campaign,
            fbclid: data.fbclid,
            gclid: data.gclid,
            referrer: data.referrer,
            utm_source: data.utm_source,
            utm_medium: data.utm_medium,
            utm_campaign: data.utm_campaign,
            utm_content: data.utm_content,
            utm_term: data.utm_term,
          }
        : { last_seen_at: now };
      await supabaseAdmin.from("visitor_attribution").update(patch).eq("id", existing.id);
      return { ok: true, channel: upgrade ? result.channel : existing.channel };
    }

    await supabaseAdmin.from("visitor_attribution").insert({
      cid: data.cid,
      channel: result.channel,
      source: result.source,
      medium: result.medium,
      campaign: result.campaign,
      fbclid: data.fbclid,
      gclid: data.gclid,
      landing_page: data.landing_page,
      referrer: data.referrer,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
      utm_content: data.utm_content,
      utm_term: data.utm_term,
      first_seen_at: now,
      last_seen_at: now,
    });

    return { ok: true, channel: result.channel };
  });
